import React from 'react';
import scheduleData from '../../data/fullSchedule.json'

const AISymposiumSchedule = ({ scheduleData, eventName = "AI Uni Conference" }) => {
  // A4 dimensions
  const a4Style = {
    width: '794px',
    minHeight: '1123px',
  };

  const DaySchedule = ({ dayData, pageNumber }) => (
    <div 
      className="bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 mx-auto my-4 shadow-2xl border border-cyan-500/20 relative overflow-hidden"
      style={a4Style}
    >
      {/* Animated background elements */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-10 left-20 w-32 h-32 bg-cyan-400 rounded-full blur-xl"></div>
        <div className="absolute bottom-20 right-32 w-40 h-40 bg-green-400 rounded-full blur-xl"></div>
        <div className="absolute top-1/2 left-1/3 w-24 h-24 bg-sky-400 rounded-full blur-lg"></div>
      </div>

      <div className="relative z-10 p-8">
        {/* Header */}
        <div className="text-center mb-8 pb-6 border-b border-cyan-400/30">
          <div className="inline-block bg-gradient-to-r from-cyan-400 to-green-400 bg-clip-text">
            <h1 className="text-3xl font-bold text-transparent mb-2">{eventName}</h1>
          </div>
          <h2 className="text-2xl font-semibold text-cyan-200">{dayData.date}</h2>
          <div className="text-sm text-cyan-300/70 mt-2">Page {pageNumber}</div>
        </div>

        {/* Schedule Items */}
        <div className="space-y-4">
          {groupParallelSessions(dayData.schedule).map((itemGroup, index) => (
            <div key={index} className="flex gap-4">
              {itemGroup.map((item, itemIndex) => (
                <ScheduleItem key={itemIndex} item={item} isParallel={itemGroup.length > 1} />
              ))}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="mt-8 pt-6 border-t border-cyan-400/20 text-center">
          <p className="text-cyan-300/60 text-sm">AI Uni Conference 2025 | Organized by CVS, DU</p>
          <div className="flex justify-center space-x-2 mt-2">
            <span className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse"></span>
            <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" style={{animationDelay: '0.2s'}}></span>
            <span className="w-2 h-2 bg-sky-400 rounded-full animate-pulse" style={{animationDelay: '0.4s'}}></span>
          </div>
        </div>
      </div>
    </div>
  );

  const ScheduleItem = ({ item, isParallel }) => {
    const isHighlight = item.id || (item.title && (
      item.title.includes('Plenary') || 
      item.title.includes('Technical') || 
      item.title.includes('Panel')
    ));
    
    const isMinor = item.title && (
      item.title.includes('Registration') ||
      item.title.includes('Lunch') ||
      item.title.includes('Coffee') ||
      item.title.includes('Break') ||
      item.title.includes('Inauguration') ||
      item.title.includes('Vote of Thanks') ||
      item.title.includes('Felicitation')
    );

    if (isMinor) {
      return (
        <div className={`w-full flex items-center justify-between bg-gray-800/0 backdrop-blur-sm rounded-lg p-2 border-none border-cyan-400/10 ${isParallel ? 'min-w-0' : ''}`}>
            <span className="text-cyan-300/70 text-sm font-medium bg-cyan-400/10 px-3 py-1 rounded-full">
              {item.time}
            </span>
            <h3 className="text-gray-200 font-semibold text-sm mt-0">{item.title}</h3>
        </div>
      );
    }

    return (
      <div className={`${isParallel ? 'flex-1 min-w-0' : 'w-full'} bg-gradient-to-br from-gray-800/60 to-gray-900/80 backdrop-blur-sm rounded-xl p-3 border border-cyan-400/20 shadow-lg hover:border-cyan-400/40 transition-all duration-300 ${isHighlight ? 'ring-1 ring-cyan-400/20' : ''}`}>
        
        {/* Header with time and title */}
        <div className="flex justify-between items-center mb-0">
          <span className={`font-mono font-bold px-3 py-1 rounded-full ${
            isHighlight 
              ? 'bg-gradient-to-r from-cyan-400 to-green-400 text-gray-900 text-sm' 
              : 'bg-cyan-400/20 text-cyan-300 text-sm'
          }`}>
            {item.time}
          </span>
          <h3 className={`text-right font-semibold max-w-md ml-4 ${
            isHighlight 
              ? 'text-xl bg-gradient-to-r from-cyan-200 to-green-200 bg-clip-text text-transparent' 
              : 'text-sm text-gray-200'
          }`}>
            {item.title}
          </h3>
        </div>

        {/* Venue and Theme */}
        {(item.venue || item.theme) && (
          <div className="my-2 space-y-1">
            {item.venue && (
              <p className="text-cyan-300/80 text-sm flex items-center">
                <span className="w-2 h-2 bg-cyan-400 rounded-full mr-2"></span>
                {item.venue}
              </p>
            )}
            {item.theme && (
              <p className="text-green-300/80 text-sm flex items-center">
                <span className="w-2 h-2 bg-green-400 rounded-full mr-2"></span>
                {item.theme}
              </p>
            )}
          </div>
        )}

        {/* People */}
        {item.people && item.people.length > 0 && (
          <div className="mt-2">
            <div className="grid grid-cols-1 gap-2">
              {item.people.map((person, idx) => (
                <div key={idx} className="flex items-center text-sm">
                  <span className="text-cyan-300/80 font-medium min-w-20">{person.type}:</span>
                  <span className="text-gray-300 ml-2">{person.name}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Session Details with Grid */}
        {item.sessionDetails && (
          <div className="mt-2">
            <div className="grid grid-cols-1 gap-3">
              {item.sessionDetails.map((session, idx) => (
                <div key={idx} className="bg-gray-800/40 rounded-lg p-3 border border-cyan-400/10">
                  <div className="flex justify-between items-start mb-2">
                    <span className="text-cyan-300 font-mono text-xs bg-cyan-400/10 px-2 py-1 rounded">
                      {session.time}
                    </span>
                    {session.index && (
                      <span className="text-xs bg-green-400/20 text-green-300 px-2 py-1 rounded">
                        Speaker {session.index}
                      </span>
                    )}
                  </div>
                  
                  {session.speaker && (
                    <p className="text-gray-200 text-sm font-medium mb-1">
                      {session.speaker}
                    </p>
                  )}
                  
                  {session.topic && (
                    <p className="text-cyan-200/80 text-sm italic">
                      "{session.topic}"
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Parallel Session Papers Grid */}
        {/* {item.sessionDetails && item.sessionDetails.some(s => s.speaker && s.topic) && (
          <div className="mt-4">
            <h4 className="text-cyan-300 font-medium mb-3 text-sm flex items-center">
              <span className="w-3 h-3 bg-sky-400 rounded-full mr-2"></span>
              Presentations
            </h4>
            <div className="grid grid-cols-1 gap-2">
              {item.sessionDetails
                .filter(session => session.speaker && session.topic)
                .map((session, idx) => (
                  <div key={idx} className="bg-gray-800/30 rounded p-2 border border-sky-400/10">
                    <p className="text-gray-300 text-xs">
                      <span className="text-cyan-300 font-medium">
                        {session.speaker.split('--')[0]?.trim() || session.speaker.split('-')[0]?.trim()}:
                      </span>
                      {" "}{session.topic}
                    </p>
                  </div>
                ))}
            </div>
          </div>
        )} */}
      </div>
    );
  };

  // Group parallel sessions (sessions happening at the same time)
  const groupParallelSessions = (schedule) => {
    const groups = [];
    let currentGroup = [];
    let lastTime = '';

    schedule.forEach((item, index) => {
      if (item.time === lastTime) {
        currentGroup.push(item);
      } else {
        if (currentGroup.length > 0) {
          groups.push([...currentGroup]);
        }
        currentGroup = [item];
        lastTime = item.time;
      }
    });

    if (currentGroup.length > 0) {
      groups.push(currentGroup);
    }

    return groups;
  };

  return (
    <div className="bg-gradient-to-br from-gray-900 to-blue-900 min-h-screen py-8">
      {/* Print Notice */}
      <div className="no-print p-4 text-center mb-4">
        <div className="inline-block bg-gradient-to-r from-cyan-400 to-green-400 p-0.5 rounded-lg">
          <div className="bg-gray-900 px-6 py-3 rounded-lg">
            <p className="text-cyan-200 font-medium">
              🚀 AI Conference Schedule - Optimized for A4 Printing (Ctrl+P)
            </p>
          </div>
        </div>
      </div>

      <div className="flex flex-col items-center">
        {scheduleData.map((dayData, index) => (
          <DaySchedule 
            key={index} 
            dayData={dayData} 
            pageNumber={index + 1} 
          />
        ))}
      </div>
      
      {/* Print Styles */}
      <style jsx>{`
        @media print {
          body {
            margin: 0;
            padding: 0;
            background: linear-gradient(135deg, #0f172a 0%, #1e3a8a 100%) !important;
          }
          .no-print {
            display: none;
          }
        }
      `}</style>
    </div>
  );
};

// Usage component
const AIScheduleDisplay = () => {
  // Your JSON data would be imported here
//   const scheduleData = [
    // ... your JSON data
//   ];

  return (
    <AISymposiumSchedule 
      scheduleData={scheduleData} 
      eventName="National Symposium on Artificial Intelligence" 
    />
  );
};

export default AIScheduleDisplay;